<template>
  <v-container fluid class="hyp-table-container pa-0">
    <v-row dense>
      <v-col class="pb-0 ma-0">
        <v-btn
          v-if="!selecting"
          color="primary"
          small
          outlined
          class="mb-2 mr-5 pull-left"
          v-on:click="handleClickAlert"
          >Create Asset Alert</v-btn
        >

        <v-btn
          color="primary"
          outlined
          small
          @click="createAsset"
          v-show="hasAssetsMgmtAdminAccess && !selecting"
          class="pull-left mr-5"
        >
          {{ labelAddNew }}
        </v-btn>

        <v-switch
          v-if="!isMapControlsFeatureUnlocked && !selecting"
          v-model="assetsEnabled"
          solo
          class="pull-left pa-0 ma-0"
          label="View on map"
          hide-details
          v-on:change="handleChangeAssetsEnabled"
        ></v-switch>

        <div class="cleafix"></div>
      </v-col>
      <v-col cols="7">
        <v-spacer></v-spacer>
      </v-col>
    </v-row>

    <v-row dense class="pa-0 ma-0 assets-filters-section">
      <v-col cols="2">
        <v-text-field
          append-icon="mdi-magnify"
          :label="labelFilterTitle"
          v-model="filterTitle"
          single-line
          hide-details
          clearable
          class="px-3 assets-filter-title"
          @input="debounceFilterTitle"
        >
        </v-text-field>
      </v-col>
      <v-col cols="2">
        <!-- data filters -->
        <v-menu offset-y :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              plain
              text
              class="hyp-text-menu-btn pull-left"
            >
              <v-icon left>
                mdi-filter
              </v-icon>
              Filters
              <v-icon right>
                mdi-menu-down
              </v-icon>
            </v-btn>
          </template>
          <v-container class="hyp-assets-filters-container">
            <!-- list of filters -->
            <v-select
              v-model="selectedFilter"
              :items="filtersList"
              item-text="text"
              item-value="value"
              label="Select Filter"
              hide-details
              class="mb-4"
            ></v-select>

            <!-- locations filter -->
            <LocationsSelector
              v-show="selectedFilter === 'assetCountry'"
              :selected-locations="selectedCountries"
              :updated-locations="updatedLocations"
              :show-regions="false"
              label-prefix="Filter"
              @change-locations="handleChangeLocations"
            ></LocationsSelector>

            <!-- asset type filter -->
            <v-select
              v-show="selectedFilter === 'assetType'"
              v-model="options.filterAssetTypeIds"
              :items="assetTypes"
              item-text="title"
              item-value="id"
              :label="labelFilterAssetTypes"
              hide-details
              multiple
              clearable
              @change="handleChangeAssetTypeFilter"
            >
              <template v-slot:selection="{ index }">
                <span v-if="index === 0">
                  {{ options.filterAssetTypeIds.length }} selected
                </span>
              </template>
              <template v-slot:item="{ item, attrs, on }">
                <v-list-item v-on="on" v-bind="attrs" #default="{ active }">
                  <v-list-item-action>
                    <v-checkbox
                      :ripple="false"
                      :input-value="active"
                    ></v-checkbox>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-row no-gutters align="center">
                        <AssetIcon
                          :icon-size="14"
                          :container-size="25"
                          :icon="item.icon"
                          :color="item.color"
                        />
                        <span>{{ item.title }}</span>
                      </v-row>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-select>

            <!-- Risk ratings filter -->
            <RiskRatingPicker
              v-if="selectedFilter === 'riskRatings'"
              label="Select Ratings"
              :multiple="false"
              :chips="false"
              :show-levels="true"
              @select="handleSelectRating"
            ></RiskRatingPicker>
          </v-container>
        </v-menu>
      </v-col>
      <v-col cols="2" v-if="isVEColumnFeatureUnlocked">
        <AssetDateRange
          title="EVENTS"
          :items="timeRangeOptions"
          compare
          v-model="activeTimeRange"
          @change="handleDateRangeChange"
          default-content
          :custom-range="options.filterDateRange"
          :custom-compare-range="options.filterDateRangeCompare"
          class="pull-left"
        ></AssetDateRange>
      </v-col>

      <v-col cols="2" v-if="isVEColumnFeatureUnlocked">
        <AssetSeveritySelector
          title="EVENTS"
          v-model="eventsSeverity"
          @input="handleEventsSeverity"
          class="pull-left ml-4"
        ></AssetSeveritySelector>
      </v-col>
      <v-col class="assets-fields-selector">
        <v-spacer></v-spacer>
        <!-- column show/hide/selector -->
        <v-menu offset-y :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              plain
              text
              class="hyp-text-menu-btn pull-right ml-4"
            >
              <v-icon left>
                mdi-table
              </v-icon>
              Fields
              <v-icon right>
                mdi-menu-down
              </v-icon>
            </v-btn>
          </template>
          <v-treeview
            v-model="selectedFields"
            :items="filteredDataFields"
            item-key="value"
            item-text="text"
            item-value="value"
            item-disabled="isSticky"
            item-children="children"
            open-on-click
            selectable
            selection-type="independent"
            multiple-active
            selected-color="primary"
            dense
            expand-icon="mdi-menu-down"
            class="hyp-assets-menu-container"
            @input="handleFieldChange"
          ></v-treeview>
        </v-menu>
      </v-col>
    </v-row>

    <v-row
      dense
      class="pa-0"
      :class="{
        'my-2':
          selectedCountriesChipMetadata ||
          selectedTypesChipMetadata ||
          selectedRatings,
      }"
    >
      <v-col>
        <!-- Type -->
        <v-chip
          v-for="(assetType, index) in selectedTypesChipMetadata"
          :key="`assetType-${index}`"
          small
          close
          class="asset-filter-chip pull-left mr-1 mb-1 px-2"
          @click:close="handleRemoveTypeFilter(assetType)"
        >
          <span :title="assetType.title">
            {{ assetType.title }}
          </span>
        </v-chip>
        <!-- Country -->
        <v-chip
          v-for="(country, index) in selectedCountriesChipMetadata"
          :key="`country-${index}`"
          small
          close
          class="asset-filter-chip pull-left mr-1 mb-1 px-2"
          @click:close="handleRemoveCountryFilter(country)"
        >
          <span :title="country.name">
            {{ country.name }}
          </span>
        </v-chip>
        <!-- Risk ratings -->
        <v-chip
          v-for="(rating, index) in selectedRatings"
          :key="`rating-${index}`"
          small
          close
          class="asset-filter-chip pull-left mr-1 mb-1 px-2"
          @click:close="handleRemoveRatingFilter(rating)"
        >
          <span :title="rating.display" class="pr-2">
            {{ rating.display }}:
          </span>
          <span
            v-for="(level, index) in rating.selectedLevels"
            :key="`level-${index}`"
          >
            <span
              v-if="index > 0 && index < rating.selectedLevels.length"
              class="mr-1"
              >,</span
            >
            <span v-html="getRiskRatingStyle(level, true)"></span>
          </span>
        </v-chip>
      </v-col>
    </v-row>

    <v-row>
      <v-col class="hyp-data-table-container">
        <v-data-table
          v-model="selected"
          dense
          v-resize="handleResize"
          :headers="filteredVisibleHeaders"
          :items="assetItems"
          :items-per-page="itemsPerPage"
          :server-items-length="totalAssets"
          sort-by="country.name"
          :height="listingHeight"
          fixed-header
          :loading="loading"
          :loading-text="labelLoading"
          class="assets-data-table"
          :class="{ selectable: !!selecting }"
          :options.sync="options"
          @update:options="handleTableOptions"
          :footer-props="footerProps"
          :show-select="!!selecting"
          @item-selected="handleItemSelected"
          @toggle-select-all="handleToggleSelectAll"
        >
          <template v-slot:item.action="{ item }">
            <v-menu>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="on"
                  width="50px"
                  height="100%"
                  class="asset-action-menu-button"
                >
                  <img
                    width="16px"
                    class="asset-action-menu-icon"
                    src="@/assets/svgs/circle-ellipsis-solid.svg"
                    alt="Asset action menu icon"
                  />
                  <v-icon>
                    mdi-chevron-down
                  </v-icon>
                </v-btn>
              </template>
              <!--Actions-->
              <v-list class="asset-action-menu">
                <v-list-item
                  v-if="isAssetToSearchFeatureUnlocked"
                  @click="handleSearchAssetRadius(item)"
                >
                  <v-list-item-title>Search Asset Radius</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="isViewOnMapFeatureUnlocked"
                  @click="flyToItem(item)"
                >
                  <v-list-item-title>View on map</v-list-item-title>
                </v-list-item>
                <v-list-item
                  :disabled="isAssetReportLoading"
                  @click="!isAssetReportLoading && generateReport(item)"
                >
                  <v-list-item-title
                    >Generate Asset Report (PDF)
                    <Loader
                      v-if="isAssetReportLoading"
                      loader-class="pull-right ml-2"
                      text=""
                      size="small"
                    ></Loader
                  ></v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="
                    typeof item.latitude === 'number' &&
                    typeof item.longitude === 'number'
                  "
                  @click="
                    $refs.copyToClipboard.copy(
                      `${item.latitude},${item.longitude}`
                    )
                  "
                >
                  <v-list-item-title>Copy Lat/Long</v-list-item-title>
                </v-list-item>
                <v-list-item
                  @click="editAsset(item)"
                  v-if="hasAssetsMgmtAdminAccess"
                >
                  <v-list-item-title>Edit</v-list-item-title>
                </v-list-item>
                <v-list-item
                  @click="removeAsset(item)"
                  v-if="hasAssetsMgmtAdminAccess"
                >
                  <v-list-item-title>Delete</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
          <template v-slot:item.country.name="{ item }">
            <div v-if="item.country && item.country.name">
              <span
                v-if="
                  (!item.country.flagUrl || !item.country.countryAlpha2Code) &&
                  item.country.name
                "
                >{{ item.country.name }}</span
              >
              <DetailsListItemCountryChip
                v-if="
                  item.country.countryAlpha2Code &&
                  item.country.name &&
                  item.country.flagUrl
                "
                :key="item.country.countryAlpha2Code"
                :name="item.country.name"
                :code="item.country.code"
                :short-display-name="item.country.shortDisplayName"
                theme="light"
              ></DetailsListItemCountryChip>
            </div>
          </template>
          <template v-slot:item.assetType.title="{ item }">
            <div
              v-if="
                item.assetType && item.assetType.icon && item.assetType.color
              "
            >
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <span v-on="on">
                    <AssetIcon
                      :icon-size="14"
                      :container-size="25"
                      :icon="item.assetType.icon"
                      :color="item.assetType.color"
                    />
                  </span>
                </template>
                <span>{{ item.assetType.title }}</span>
              </v-tooltip>
            </div>
          </template>
          <template v-slot:item.title="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <div class="asset-title">
                    {{ item.title }}
                  </div>
                </span>
              </template>
              <span>{{ item.title }}</span>
            </v-tooltip>
          </template>
          <template v-slot:item.verifiedEventsDelta="{ item }">
            <AssetDelta
              :item="item"
              headerKey="verifiedEventsDelta"
              percentHeaderKey="verifiedEventsDeltaPct"
              showPercentage
              :disableTooltip="true"
            ></AssetDelta>
          </template>
          <template v-slot:item.veCrimeDelta="{ item }">
            <AssetDelta
              :item="item"
              headerKey="veCrimeDelta"
              percentHeaderKey="veCrimeDeltaPct"
              showPercentage
              :disableTooltip="true"
            ></AssetDelta>
          </template>
          <template v-slot:item.veTerrorismDelta="{ item }">
            <AssetDelta
              :item="item"
              headerKey="veTerrorismDelta"
              percentHeaderKey="veTerrorismDeltaPct"
              showPercentage
              :disableTooltip="true"
            ></AssetDelta>
          </template>
          <template v-slot:item.veUnrestDelta="{ item }">
            <AssetDelta
              :item="item"
              headerKey="veUnrestDelta"
              percentHeaderKey="veUnrestDeltaPct"
              showPercentage
              :disableTooltip="true"
            ></AssetDelta>
          </template>
          <template v-slot:item.veWarDelta="{ item }">
            <AssetDelta
              :item="item"
              headerKey="veWarDelta"
              percentHeaderKey="veWarDeltaPct"
              showPercentage
              :disableTooltip="true"
            ></AssetDelta>
          </template>
          <template v-slot:item.pulseScore="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="pull-left pulse-cell"
                  style="height: 18px;"
                  v-bind="attrs"
                  v-on="on"
                >
                  <div
                    class="pull-left mr-1 text-center pulse-circle"
                    :style="{
                      color: item.pulseColor,
                      'background-color': item.pulseColor,
                    }"
                  ></div>
                  <div class="pull-left pulse-score">
                    {{ item.pulseScore ? Math.floor(item.pulseScore) : "" }}
                  </div>
                  <div class="clearfix"></div>
                </div>
              </template>
              <span>{{ item.pulseLocationName }}</span>
            </v-tooltip>
          </template>
          <template v-slot:item.pulseDelta1d="{ item }">
            <AssetDelta
              :item="item"
              headerKey="pulseDelta1d"
              tooltipKey="pulseLocationName"
            ></AssetDelta>
          </template>
          <template v-slot:item.pulseDelta7d="{ item }">
            <AssetDelta
              :item="item"
              headerKey="pulseDelta7d"
              tooltipKey="pulseLocationName"
            ></AssetDelta>
          </template>
          <template v-slot:item.pulseDelta30d="{ item }">
            <AssetDelta
              :item="item"
              headerKey="pulseDelta30d"
              tooltipKey="pulseLocationName"
            ></AssetDelta>
          </template>
          <template v-slot:item.monitorRadius="{ item }">
            <span>{{ formatMonitorRadius(item.monitorRadius) }}</span>
          </template>
          <template
            v-for="(header, index) in filteredVisibleRiskRatingHeaders"
            #[`item.${header.value}`]="{value}"
          >
            <span
              :key="`rrheader-${index}`"
              v-html="getRiskRatingStyle(value)"
            ></span>
          </template>
          <template v-slot:no-data>
            <div class="my-5">
              There are currently no assets. <br /><br />
              <div v-show="hasAssetsMgmtAdminAccess">
                You can create a new asset by clicking the <br />
                Create New Asset button above.
              </div>
            </div>
          </template>
          <template v-slot:no-results>
            <div class="my-5">
              Your search returned no results.
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- CREATE/EDIT ASSET DIALOG -->
    <v-dialog
      v-model="dialog"
      max-width="1000px"
      content-class="asset-dialog"
      scrollable
    >
      <v-card>
        <v-card-title>
          <v-row>
            <v-col>
              <span>{{ formTitle }}</span>
            </v-col>
            <v-col cols="1">
              <v-icon right v-on:click="close">mdi-close</v-icon>
            </v-col>
          </v-row>
        </v-card-title>

        <v-card-text style="height: 650px;">
          <v-container class="pt-0">
            <v-form ref="form" v-model="validForm">
              <v-row>
                <v-col cols="6" class="pt-0">
                  <v-text-field
                    v-model="asset.title"
                    :counter="100"
                    :label="labelName"
                    :rules="nameRules"
                    required
                  ></v-text-field>
                  <v-menu
                    ref="menu"
                    v-model="menu"
                    :close-on-content-click="false"
                    :return-value.sync="asset.expirationDate"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="expirationDateFormatted"
                        label="Expiration Date"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="asset.expirationDate"
                      no-title
                      scrollable
                      :show-current="utcDate"
                      :min="utcDate"
                    >
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="menu = false">
                        Cancel
                      </v-btn>
                      <v-btn
                        text
                        color="primary"
                        @click="$refs.menu.save(asset.expirationDate)"
                      >
                        OK
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="6" class="pt-0">
                  <v-select
                    v-model="asset.assetTypeId"
                    :items="sortedAssetTypes"
                    item-text="title"
                    item-value="id"
                    :label="labelType"
                    :rules="requiredRules"
                    class="asset-types"
                  >
                    <template v-slot:selection="{ item }">
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>
                            <v-row no-gutters align="center">
                              <AssetIcon
                                :icon-size="14"
                                :container-size="25"
                                :icon="item.icon"
                                :color="item.color"
                              />
                              <span>{{ item.title }}</span>
                            </v-row>
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>

                    <template v-slot:item="{ item, attrs, on }">
                      <v-list-item v-on="on" v-bind="attrs">
                        <v-list-item-content class="py-2">
                          <v-list-item-title>
                            <v-row no-gutters align="center">
                              <AssetIcon
                                :icon-size="14"
                                :container-size="25"
                                :icon="item.icon"
                                :color="item.color"
                              />
                              <span>{{ item.title }}</span>
                            </v-row>
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                  <MonitorRadiusField
                    :monitorRadius="assetRadius"
                    :poisDistUnits="this.preferences.poisDistUnits"
                    @update-radius="updateMonitorRadius"
                  ></MonitorRadiusField>
                </v-col>
              </v-row>

              <v-row>
                <v-divider class="py-2"></v-divider>
              </v-row>

              <v-row>
                <v-col cols="12" class="pt-0">
                  <AssetsEditor
                    :asset="asset"
                    @change="handleOnChange"
                  ></AssetsEditor>
                </v-col>
              </v-row>

              <v-row class="location-textfields">
                <v-col cols="6">
                  <v-text-field
                    v-model="asset.fullAddress"
                    type="text"
                    label="Address"
                    disabled
                  ></v-text-field>
                  <v-text-field
                    v-model.number="asset.latitude"
                    type="number"
                    step="any"
                    :rules="latitudeRules"
                    label="Latitude"
                    disabled
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="asset.country.name"
                    type="text"
                    label="Country"
                    disabled
                  ></v-text-field>
                  <v-text-field
                    v-model.number="asset.longitude"
                    type="number"
                    step="any"
                    :rules="longitudeRules"
                    label="Longitude"
                    required
                    disabled
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row> <v-divider class="py-2"></v-divider> </v-row>
              <!-- Dynamically display custom fields -->
              <v-row>
                <v-col
                  cols="6"
                  v-for="(field, index) in sortedEnabledCustomFields"
                  v-bind:key="index"
                >
                  <v-textarea
                    :label="field.title"
                    :rows="1"
                    auto-grow
                    :counter="750"
                    v-model="asset.customFields[field.title]"
                    @input="
                      updateCustomField(
                        asset.customFields,
                        `${field.title}`,
                        $event
                      )
                    "
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text class="default btn mr-5" @click="close">
            Cancel
          </v-btn>
          <v-btn
            class="primary btn mr-3"
            @click="saveAsset"
            :disabled="!validForm || saving"
          >
            Save
            <v-progress-circular
              v-if="saving"
              indeterminate
              size="14"
              width="2"
              class="ml-2"
            ></v-progress-circular>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DELETE ASSET DIALOG -->
    <v-dialog v-model="dialogDelete" max-width="500px">
      <v-card>
        <v-card-title>
          Are you sure you want to delete asset: {{ asset.title }}?
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="default" text @click="closeDelete">Cancel</v-btn>
          <v-btn
            :disabled="deleting"
            class="primary"
            text
            @click="deleteAssetConfirm"
          >
            Delete
            <v-progress-circular
              v-if="deleting"
              indeterminate
              size="14"
              width="2"
              class="ml-2"
            ></v-progress-circular>
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <CopyToClipboard ref="copyToClipboard"></CopyToClipboard>
  </v-container>
</template>

<script>
import lodashGet from "lodash/get";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashRemove from "lodash/remove";
import lodashDebounce from "lodash/debounce";
import lodashFind from "lodash/find";
import lodashIsEqual from "lodash/isEqual";

import { mapGetters } from "vuex";
import { ASSET_CUSTOM_AOI } from "@/common/constants/assetManagement";
import {
  NOTIFICATION_ASSET_TYPE_VERIFIED_EVENT,
  NOTIFICATION_FREQUENCY_IMMEDIATELY,
  NOTIFICATION_EXPIRATION_ID_NEVER,
} from "@/common/constants/notifications";
import { LABELS } from "@/common/constants/labels";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import {
  DATE_FORMAT,
  getFormattedDate,
  getRelativeDate,
} from "@/common/date.utilities";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import {
  MODULE_PREFIX_TIME,
  MODULE_PREFIX_ASSETS,
  MODULE_PREFIX_USER,
  MODULE_PREFIX_NOTIFICATIONS,
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_HOME,
} from "@/store";
import {
  SAVE_PREFERENCES,
  GET_ASSETS_REFRESH_MAP,
  GET_ASSETS,
  GET_ASSET_TYPES,
  GET_ASSET_CUSTOM_FIELDS,
  SAVE_ASSET,
  DELETE_ASSET,
  GET_ASSETS_SELECTED,
  GET_NOTIFICATION,
  GET_MODAL,
  GET_ASSETS_TABLE_OPTIONS,
  GET_DETAILS_SIDEBAR,
  GET_SELECTOR_PANEL,
} from "@/store/actions.type";
import {
  getAllCountries,
  getCityFromGooglePlace,
  getCountryFromGooglePlace,
} from "@/common/maps/utilities";
import { formatDisplayMonitorRadius } from "@/common/basic.utilities";
import { ASSET_ICONS } from "@/common/constants/assetManagement";
import {
  hasAssetsMgmtAdminAccess,
  hasEssentialsAccess,
} from "@/services/user/user.service";
import { getAssetReport } from "@/services/reporting/reporting.service";
import {
  RISK_LEVELS,
  RISK_RATING_PRIMARIES,
} from "@/common/constants/controlRisks/risks";
import { getRiskRatingCategories } from "@/services/controlRisks/risks.service";
import { TIME_OPTIONS } from "@/common/constants/assetManagement";

const sortOptions = {
  NONE: "",
  ASCENDING: "ASC",
  DESCENDING: "DESC",
};

const headerOptionValues = {
  COUNTRY_NAME: "country.name",
  TITLE: "title",
  ASSET_TYPE: "assetType.title",
  CUSTOM_FIELD: "customField",
  MONITOR_RADIUS: "monitorRadius",
  HOTSPOTS: "hotspotCount",
  PULSE: "pulseScore",
  PULSE_DELTA_1d: "pulseDelta1d",
  PULSE_DELTA_7d: "pulseDelta7d",
  PULSE_DELTA_30d: "pulseDelta30d",
  RISK_RATINGS: "ratings",
  VERIFIED_EVENTS: "verifiedEvents",
  VERIFIED_EVENTS_DELTA: "verifiedEventsDelta",
  VERIFIED_EVENTS_WAR: "veWar",
  VERIFIED_EVENTS_WAR_DELTA: "veWarDelta",
  VERIFIED_EVENTS_TERRORISM: "veTerrorism",
  VERIFIED_EVENTS_TERRORISM_DELTA: "veTerrorismDelta",
  VERIFIED_EVENTS_UNREST: "veUnrest",
  VERIFIED_EVENTS_UNREST_DELTA: "veUnrestDelta",
  VERIFIED_EVENTS_CRIME: "veCrime",
  VERIFIED_EVENTS_CRIME_DELTA: "veCrimeDelta",
};

const EMITTED_ACTIONS = {
  CLICK_CLOSE: "click-close",
};

export default {
  name: "AssetsList",
  components: {
    AssetDelta: () => import("@/components/assetManagement/AssetDelta"),
    AssetDateRange: () => import("@/components/assetManagement/AssetDateRange"),
    AssetSeveritySelector: () =>
      import("@/components/assetManagement/AssetSeveritySelector"),
    CopyToClipboard: () => import("@/components/common/CopyToClipboard"),
    AssetsEditor: () => import("@/components/assetManagement/AssetsEditor"),
    AssetIcon: () => import("@/components/assetManagement/AssetIcon"),
    LocationsSelector: () => import("@/components/common/LocationsSelector"),
    RiskRatingPicker: () =>
      import("@/components/controlRisks/risks/RiskRatingPicker"),
    DetailsListItemCountryChip: () =>
      import("@/components/common/details/DetailsListItemCountryChip"),
    Loader: () => import("@/components/common/Loader"),
    MonitorRadiusField: () => import("@/components/common/MonitorRadiusField"),
  },
  props: {
    selecting: {
      type: String,
    },
    filtersSelected: {
      type: Object,
    },
    alertId: {
      type: String,
    },
    selectedAssets: {
      type: Array,
    },
  },
  data: function () {
    return {
      isAssetReportLoading: false,
      validForm: false,
      selected: [],
      selectAll: false,
      assetRadius: 0,
      actionsHeader: {
        text: "Actions",
        value: "action",
        sortable: false,
        width: 60,
        maxWidth: "60",
        selectable: false,
      },
      labelFilterTitle: `Search My ${LABELS.assets}`,
      labelFilterAssetTypes: "Filter Asset Types",
      labelFilterCountries: "Filter Countries",
      labelAddNew: `Add New ${LABELS.asset}`,
      labelLoading: `Retrieving ${LABELS.assets}`,
      labelName: `${LABELS.asset} name`,
      labelType: `${LABELS.asset} type`,
      loading: false,
      countries: null,
      inputTextCountries: "",
      filterTitle: "",
      windowSize: {
        x: 0,
        y: 0,
      },
      dialog: false,
      dialogDelete: false,
      deleting: false,
      saving: false,
      editedIndex: -1,
      emptyAsset: {
        title: "",
        assetType: {
          color: "",
          icon: "",
          id: null,
          name: "",
        },
        latitude: 0,
        longitude: 0,
        fullAddress: null,
        country: {
          name: "",
          code: "",
        },
        city: "",
        googlePlace: null,
        isSystemType: false,
        enabled: false,
        customFields: {},
        expirationDate: null,
        monitorRadius: 10,
      },
      asset: {
        title: "",
        assetType: {
          color: "",
          icon: "",
          id: null,
          name: "",
        },
        latitude: 0,
        longitude: 0,
        fullAddress: null,
        country: {
          name: "",
          code: "",
        },
        city: "",
        googlePlace: null,
        isSystemType: false,
        enabled: false,
        customFields: {},
        expirationDate: null,
        monitorRadius: 10,
      },
      nameRules: [
        (value) => !!value || "Required.",
        (value) => (value || "").length <= 100 || "Max 100 characters",
      ],
      latitudeRules: [
        (value) => !!value || "Required.",
        (value) => value >= -90 || "Greater than -90",
        (value) => value <= 90 || "Less than 90",
      ],
      longitudeRules: [
        (value) => !!value || "Required.",
        (value) => value >= -180 || "Greater than -180",
        (value) => value <= 180 || "Less than 180",
      ],
      requiredRules: [(value) => !!value || "Required."],
      assetsEnabled: false,
      aoiFeature: ASSET_CUSTOM_AOI,
      menu: false,
      hasAssetsMgmtAdminAccess: hasAssetsMgmtAdminAccess(),
      isFormVisible: false,
      options: {
        filterTitle: null,
        filterCountries: [],
        filterAssetTypeIds: [],
        filterRatings: {},
        filterAlertId: null,
        filterDateRange: {},
        sortField: null,
        sortDirection: sortOptions.NONE,
        eventSeverity: null,
      },
      pageNum: 1,
      itemsPerPage: 50,
      totalAssets: 0,
      icons: ASSET_ICONS,
      selectedFields: [
        headerOptionValues.ASSET_TYPE,
        headerOptionValues.TITLE,
        headerOptionValues.COUNTRY_NAME,
      ],
      selectedFilter: null,
      filtersList: [
        {
          text: "Asset Type",
          value: "assetType",
        },
        {
          text: "Country",
          value: "assetCountry",
        },
        {
          text: "Risk Ratings",
          value: "riskRatings",
        },
      ],
      updatedLocations: null,
      riskRatingCategories: [],
      hasEssentials: hasEssentialsAccess(),
      timeRangeOptions: TIME_OPTIONS,
      activeTimeRange: TIME_OPTIONS[3].value,
      eventsSeverity: null,
      isInitialized: false,
    };
  },
  async mounted() {
    this.setSelectedAssets();
    await this.getAssetTypes();
    await this.getCustomFields();
    this.countries = await getAllCountries();
    this.riskRatingCategories = await getRiskRatingCategories();
    this.applyPrefs();
  },
  computed: {
    ...mapGetters({
      utcDate: MODULE_PREFIX_TIME + "today",
      preferences: MODULE_PREFIX_USER + "preferences",
      assets: MODULE_PREFIX_ASSETS + "assets",
      assetTypes: MODULE_PREFIX_ASSETS + "assetTypes",
      customFields: MODULE_PREFIX_ASSETS + "assetCustomFields",
      assetsSelected: MODULE_PREFIX_ASSETS + "assetsSelected",
      modal: MODULE_PREFIX_NOTIFICATIONS + "modal",
      notification: MODULE_PREFIX_NOTIFICATIONS + "notification",
      maps: MODULE_PREFIX_MAP + "maps",
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
      selectorPanel: MODULE_PREFIX_HOME + "selectorPanel",
    }),
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    isVEColumnFeatureUnlocked() {
      return this.featureUnlocked("assets-table-ve");
    },
    isViewOnMapFeatureUnlocked() {
      return this.featureUnlocked("assets-table-view-on-map");
    },
    isAssetToSearchFeatureUnlocked() {
      return this.featureUnlocked("assets-table-to-search");
    },
    isMapControlsFeatureUnlocked() {
      return this.featureUnlocked("map-controls");
    },
    listingHeight() {
      let height = this.windowSize.y - 350;
      if (this.selecting) {
        height -= 200;
      }
      return height;
    },
    sortedAssetTypes() {
      if (this.assetTypes) {
        return this.assetTypes.filter((assetType) => {
          return assetType.enabled;
        });
      }
      return null;
    },
    sortedEnabledCustomFields() {
      if (this.customFields) {
        return this.customFields.filter((field) => {
          return field.enabled;
        });
      }
      return null;
    },
    headers() {
      const customHeaders = this.sortedEnabledCustomFields.map((field) => {
        return {
          text: field.title,
          value: "customFields." + field.title,
          sortable: true,
          width: 200,
        };
      });
      return [this.actionsHeader, ...this.initialHeaders(), ...customHeaders];
    },
    filteredDataFields() {
      return this.headers.filter((field) => {
        return field.selectable !== false && !field.riskRatingParentId;
      });
    },
    filteredVisibleHeaders() {
      return this.headers
        .flatMap((header) => {
          if (header.children) {
            return [header, ...header.children];
          }
          return header;
        })
        .filter((field) => {
          return (
            this.selectedFields.includes(field.value) ||
            field.value === "action"
          );
        });
    },
    filteredVisibleRiskRatingHeaders() {
      return this.filteredVisibleHeaders.filter((header) => {
        return header.isRiskRating;
      });
    },
    formTitle() {
      return this.editedIndex === -1
        ? `New ${LABELS.asset}`
        : `Edit ${LABELS.asset}`;
    },
    expirationDateFormatted() {
      if (this.asset?.expirationDate) {
        return getFormattedDate(this.asset.expirationDate);
      }
      return null;
    },
    selectedCountries() {
      return { countries: this.options.filterCountries };
    },
    selectedRatings() {
      return this.riskRatingCategories?.reduce((accum, rating) => {
        if (this.options.filterRatings[rating.dbName]) {
          rating.selectedLevels = this.options.filterRatings[rating.dbName];
          accum.push(rating);
        }
        return accum;
      }, []);
    },
    selectedCountriesChipMetadata() {
      return this.countries
        ?.filter((country) => {
          return this.selectedCountries.countries.includes(
            country.properties.iso2
          );
        })
        ?.map((c) => ({
          name: c.properties.displayName,
          isoAlpha2Code: c.properties.iso2,
          countryAlpha2Code: c.properties.iso2,
          shortDisplayName: c.properties.iso3.toUpperCase(),
          code: c.properties.iso3.toUpperCase(),
        }));
    },
    selectedTypesChipMetadata() {
      return this.assetTypes?.filter((type) => {
        return this.options.filterAssetTypeIds.includes(type.id);
      });
    },
    assetItems() {
      return this.assets?.assets ?? [];
    },
    footerProps() {
      if (this.selecting) {
        return { "items-per-page-options": [1000] };
      }
      return { "items-per-page-options": [25, 50, 100, 500, 1000] };
    },
  },
  methods: {
    applyPrefs() {
      this.filterTitle = this.options.filterTitle;
      // setting severy from preferences
      this.eventsSeverity = this.options.eventsSeverity;
      this.activeTimeRange =
        this.preferences?.assets?.filters?.activeItem ?? this.activeTimeRange;
      if (this.preferences?.assets?.filters?.selectedFields) {
        this.selectedFields =
          this.preferences.assets.filters.selectedFields ?? this.selectedFields;
        this.isInitialized = true;
      }
    },
    toFormattedDateRange({ isRelative, start, end }) {
      return {
        start: getRelativeDate(isRelative, start).format(DATE_FORMAT),
        end: getRelativeDate(isRelative, end).format(DATE_FORMAT),
      };
    },
    setSelectedAssets() {
      if (this.selectedAssets) {
        this.$store.dispatch(
          MODULE_PREFIX_ASSETS + GET_ASSETS_SELECTED,
          this.selectedAssets
        );
      }
    },
    async getAssetTypes() {
      await this.$store.dispatch(MODULE_PREFIX_ASSETS + GET_ASSET_TYPES, {});
    },
    async getCustomFields() {
      await this.$store.dispatch(
        MODULE_PREFIX_ASSETS + GET_ASSET_CUSTOM_FIELDS,
        {}
      );
    },
    getRiskRatingStyle(ratingValue, nameOnly) {
      const riskLevel = this.getRiskLevel(ratingValue);
      if (!riskLevel) {
        return "No content";
      }
      if (nameOnly) {
        return riskLevel.name;
      }
      const htmlRatings = `
            <div class="pull-left" style="height:18px;">
              <div class="pull-left mr-2" style="position:relative; top:3px; width:12px; height:12px; border-radius:12px; color:${riskLevel.fontColor}; background-color:${riskLevel.bgColor};"></div>
              <div class="pull-left mr-2" style="position:relative; top:0px;">${riskLevel.name}</div>
            </div>
          `;
      return htmlRatings;
    },
    getRiskLevel(ratingValue) {
      if (ratingValue) {
        const rating = lodashFind(RISK_LEVELS, { value: ratingValue });
        return rating;
      }
      return null;
    },
    handleResize() {
      this.windowSize = { x: window.innerWidth, y: window.innerHeight };
    },
    async handleFieldChange() {
      await this.savePrefs();
    },
    async handleSelectRating(rating) {
      if (rating.selectedLevels) {
        this.options.filterRatings = {
          ...this.options.filterRatings,
          [rating.dbName]: rating.selectedLevels,
        };

        await this.savePrefs();

        //To show ratings column on filter selection
        if (!this.selectedFields.includes(rating.value)) {
          this.selectedFields.push(rating.value);
        }
      }
    },
    async handleChangeLocations(locations) {
      if (locations?.countries) {
        this.options.filterCountries = locations.countries.map((country) => {
          return country.value || country;
        });
      }
      await this.savePrefs();
    },
    async handleRemoveCountryFilter(country) {
      const countryIndex = this.options.filterCountries.indexOf(
        country.isoAlpha2Code
      );
      if (countryIndex > -1) {
        this.options.filterCountries.splice(countryIndex, 1);
      }
      this.updatedLocations = {
        countries: lodashCloneDeep(this.options.filterCountries),
      };
      await this.savePrefs();
    },
    async handleRemoveTypeFilter(type) {
      const typeIndex = this.options.filterAssetTypeIds.indexOf(type.id);
      if (typeIndex > -1) {
        this.options.filterAssetTypeIds.splice(typeIndex, 1);
      }
      await this.savePrefs();
    },
    async handleRemoveRatingFilter(rating) {
      delete this.options.filterRatings[rating.dbName];
      // To trigger watch/computed
      this.options.filterRatings = lodashCloneDeep(this.options.filterRatings);
      await this.savePrefs();
    },
    async handleEventsSeverity(eventsSeverity) {
      this.options = {
        ...this.options,
        eventsSeverity: eventsSeverity,
      };
      await this.savePrefs();
    },
    handleSearchAssetRadius(item) {
      const selectorPanel = lodashCloneDeep(this.selectorPanel);
      selectorPanel.selected = "search";
      selectorPanel.visible = true;
      selectorPanel.isSelected = true;
      selectorPanel.searchWithAssetRadius = item;
      this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_SELECTOR_PANEL,
        selectorPanel
      );
    },
    updateMonitorRadius(value) {
      this.asset.monitorRadius = value;
    },
    debounceFilterTitle: lodashDebounce(async function (event) {
      this.options = {
        ...this.options,
        filterTitle: event,
      };
      await this.savePrefs();
    }, 750),
    createAsset() {
      this.reset();
      this.asset = lodashCloneDeep(this.emptyAsset);
      this.assetRadius = this.asset.monitorRadius;
      this.dialog = true;
    },
    editAsset(item) {
      this.editedIndex = this.assets.assets.indexOf(item);
      this.asset = { ...item };
      this.assetRadius = this.asset.monitorRadius;
      this.asset.country = {
        name: item.country ? item.country.name : "",
        code: item.country.countryAlpha2Code
          ? item.country.countryAlpha2Code
          : "",
      };
      this.asset.customFields = item.customFields ? item.customFields : {};
      this.dialog = true;
    },
    removeAsset(item) {
      this.editedIndex = this.assets.assets.indexOf(item);
      this.asset = Object.assign({}, item);
      if (!this.asset.country) {
        this.asset.country = {
          name: "",
          code: "",
        };
      }
      this.dialogDelete = true;
    },
    async deleteAssetConfirm() {
      this.deleting = true;
      await this.$store.dispatch(
        MODULE_PREFIX_ASSETS + DELETE_ASSET,
        this.asset.id
      );
      this.closeDelete();
      this.deleting = false;
      await this.refreshMap();
      await this.$store.dispatch(MODULE_PREFIX_ASSETS + GET_ASSETS, {
        options: this.options,
      });
    },
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.reset();
        this.editedIndex = -1;
      });
    },
    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.reset();
        this.editedIndex = -1;
      });
    },
    async saveAsset() {
      this.isRadiusTransform = true;
      if (this.$refs.form.validate()) {
        this.saving = true;
        await this.$store.dispatch(
          MODULE_PREFIX_ASSETS + SAVE_ASSET,
          this.asset
        );
        this.close();
        this.saving = false;
        this.assetMonitorRadius = 0;
        await this.refreshMap();
      } else {
        console.error("The form is not valid");
      }
    },
    getLocationFromPlace(googlePlace) {
      if (googlePlace.provided_location) {
        return googlePlace.provided_location;
      } else if (googlePlace.geometry?.location) {
        return {
          lat: googlePlace.geometry?.location?.lat(),
          lng: googlePlace.geometry?.location?.lng(),
        };
      }
    },
    handleOnChange(googlePlace) {
      this.asset.fullAddress = null;
      this.asset.country = null;
      this.asset.longitude = null;
      this.asset.latitude = null;
      this.asset.city = null;

      if (googlePlace) {
        if (googlePlace.formatted_address) {
          this.asset.fullAddress = googlePlace.formatted_address;
        }
        const { lng, lat } = this.getLocationFromPlace(googlePlace);
        this.asset.longitude = lng?.toFixed(5);
        this.asset.latitude = lat?.toFixed(5);
        this.asset.country = getCountryFromGooglePlace(googlePlace);
        this.asset.city = getCityFromGooglePlace(googlePlace);
      }
      // Hong Kong is no longer a country so here we are changing the country
      // from HK to China. (Based on ticket AAAS-7532)
      if (
        this.asset.country.code === "HK" &&
        this.asset.country.name === "Hong Kong"
      ) {
        this.asset.country.code = "CN";
        this.asset.country.name = "China";
      }
      this.$refs.form.validate();
    },
    reset() {
      this.asset = lodashCloneDeep(this.emptyAsset);
      this.assetRadius = 0;
      if (this.$refs.form) {
        this.$refs.form.reset();
      }
    },
    async handleChangeAssetsEnabled() {
      if (this.assetsEnabled) {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.assets,
          EVENT_ACTIONS.clicked,
          "Assets Enabled"
        );
      }
      await this.savePrefs();
    },
    async refreshMap(refreshFilters) {
      if (
        this.assetsEnabled ||
        (this.isMapControlsFeatureUnlocked && refreshFilters)
      ) {
        await this.$store.dispatch(
          MODULE_PREFIX_ASSETS + GET_ASSETS_REFRESH_MAP,
          {
            filterTitle: this.options.filterTitle,
            filterCountries: this.options.filterCountries,
            filterAssetTypeIds: this.options.filterAssetTypeIds,
            filterRatings: this.options.filterRatings,
            filterDateRange: this.options.filterDateRange,
          }
        );
      } else {
        await this.$store.dispatch(
          MODULE_PREFIX_ASSETS + GET_ASSETS_REFRESH_MAP,
          false
        );
      }
    },
    async savePrefs() {
      let preferences = lodashCloneDeep(this.preferences);
      preferences.assets = preferences.assets || {};
      preferences.assets.enabled = this.assetsEnabled;
      // saving assets preferences filters options
      preferences.assets.filters = this.buildFiltersFromOptions(this.options);
      preferences.assets.filters.activeItem = this.activeTimeRange;
      preferences.assets.filters.selectedFields = this.selectedFields;
      await this.$store.dispatch(
        MODULE_PREFIX_USER + SAVE_PREFERENCES,
        preferences
      );
      preferences = null;
    },
    buildFiltersFromOptions(options) {
      return {
        title: options.filterTitle,
        assetTypeIds: options.filterAssetTypeIds,
        countries: options.filterCountries,
        ratings: options.filterRatings,
        dateRange: options.filterDateRange,
        dateRangeCompare: options.filterDateRangeCompare,
        eventsSeverity: options.eventsSeverity,
      };
    },
    updateOptionsFromFilters() {
      let filterDateRange = null;
      const {
        title,
        assetTypeIds,
        ratings,
        countries,
        dateRange,
        dateRangeCompare,
        eventsSeverity,
        activeItem,
      } = this.preferences?.assets?.filters || {};

      // if the user have already define an active time for its preferences and is not a custome date, take that range.
      if (activeItem && activeItem.isRelative === true) {
        filterDateRange = this.toFormattedDateRange(activeItem);
      }

      // if the user hasn't defined any preferences for an activeItem and dateRange yet,
      // set the date range to its default last 30 days value.
      if (!dateRange) {
        filterDateRange = this.toFormattedDateRange(this.activeTimeRange);
      }

      // Update options, keeping existing values where filters are undefined
      this.options = {
        ...this.options,
        filterTitle: title ?? this.options.filterTitle,
        filterAssetTypeIds: assetTypeIds ?? this.options.filterAssetTypeIds,
        filterRatings: ratings ?? this.options.filterRatings,
        filterCountries: countries ?? this.options.filterCountries,
        filterDateRange: filterDateRange ?? dateRange,
        filterDateRangeCompare:
          dateRangeCompare ?? this.options.filterDateRangeCompare,
        eventsSeverity: eventsSeverity ?? this.options.eventsSeverity,
      };
    },
    updateCustomField(obj, prop, event) {
      if (!!event) {
        this.$set(obj, prop, event);
      }
    },
    async handleDateRangeChange(dates) {
      this.options = {
        ...this.options,
        filterDateRange: {
          start: dates.range.start.format(DATE_FORMAT),
          end: dates.range.end.format(DATE_FORMAT),
        },
        filterDateRangeCompare: dates.compare
          ? {
              start: dates.compare.start.format(DATE_FORMAT),
              end: dates.compare.end.format(DATE_FORMAT),
            }
          : null,
      };
      this.activeTimeRange = dates.activeItem;
      await this.savePrefs();
    },
    async handleChangeAssetTypeFilter() {
      await this.savePrefs();
    },
    handleItemSelected(selected) {
      if (selected && selected.item) {
        const assetsSelected = lodashCloneDeep(this.assetsSelected);
        if (selected.value) {
          assetsSelected.push(selected.item);
        } else {
          lodashRemove(assetsSelected, (item) => item.id === selected.item.id);
        }
        this.$store.dispatch(
          MODULE_PREFIX_ASSETS + GET_ASSETS_SELECTED,
          assetsSelected
        );
      }
    },
    handleToggleSelectAll(selected) {
      if (selected && selected.items) {
        const assetsSelected = selected.value ? selected.items : [];
        this.$store.dispatch(
          MODULE_PREFIX_ASSETS + GET_ASSETS_SELECTED,
          assetsSelected
        );
      }
    },
    handleClickAssetSelect() {
      const assetsSelected = [];
      this.assets.assets.forEach((asset) => {
        if (asset?.selected) {
          assetsSelected.push(asset);
        }
      });
      this.$store.dispatch(
        MODULE_PREFIX_ASSETS + GET_ASSETS_SELECTED,
        assetsSelected
      );
    },
    async handleClickAlert() {
      const notification = lodashCloneDeep(this.notification) || {};
      notification.formatted = notification.formatted || {};
      notification.formatted.docType = NOTIFICATION_ASSET_TYPE_VERIFIED_EVENT;
      notification.formatted.frequency = NOTIFICATION_FREQUENCY_IMMEDIATELY;
      notification.formatted.expiration = NOTIFICATION_EXPIRATION_ID_NEVER;
      notification.formatted.expirationDate = null;
      await this.$store.dispatch(
        MODULE_PREFIX_NOTIFICATIONS + GET_NOTIFICATION,
        notification
      );

      const modal = lodashCloneDeep(this.modal);
      modal.visible = true;
      modal.type = NOTIFICATION_ASSET_TYPE_VERIFIED_EVENT;
      await this.$store.dispatch(
        MODULE_PREFIX_NOTIFICATIONS + GET_MODAL,
        modal
      );
    },
    async handleTableOptions(event) {
      const {
        sortBy: [sortByField],
        sortDesc: [isSortDesc],
        page,
        itemsPerPage,
      } = event;
      this.options.page = page;
      this.options.itemsPerPage =
        itemsPerPage === -1 ? this.totalAssets : itemsPerPage;

      this.options.sortDirection =
        isSortDesc === undefined
          ? sortOptions.NONE
          : isSortDesc
          ? sortOptions.DESCENDING
          : sortOptions.ASCENDING;
      this.options.sortField = sortByField;

      if (this.alertId) {
        this.options.sortDirection = sortOptions.DESCENDING;
      }
      await this.$store.dispatch(
        MODULE_PREFIX_ASSETS + GET_ASSETS_TABLE_OPTIONS,
        this.options
      );
    },
    async generateReport(item) {
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.assets,
        EVENT_ACTIONS.clicked,
        `Asset Generate Asset Report (PDF) Click --- ${item.id}`
      );

      const requestTimestamp = new Date().toISOString();
      const { filterDateRange } = this.options;
      this.isAssetReportLoading = true;
      const unit = this.preferences.poisDistUnits === "miles" ? "mi" : "km";

      await getAssetReport(
        {
          assetId: item.id,
          monitorRadiusUnit: unit,
          eventsStartDate: filterDateRange?.start,
          eventsEndDate: filterDateRange?.end,
        },
        requestTimestamp
      );
      this.isAssetReportLoading = false;
    },
    async flyToItem(item) {
      if (!this.assetsEnabled) {
        this.assetsEnabled = true;
        await this.savePrefs();
        await this.refreshMap();
      }

      this.map.flyTo({
        center: [item.longitude, item.latitude],
        zoom: 9,
        padding: {
          top: 100,
          bottom: 100,
          left: 100,
          right: (this.detailsSidebar.width || 0) + 100,
        },
      });
      this.$emit(EMITTED_ACTIONS.CLICK_CLOSE);
      this.detailsSidebar.features = [
        {
          properties: {
            gid: item.id,
            dateRange: this.options.filterDateRange,
            title: item.title,
            latitude: item.latitude,
            longitude: item.longitude,
          },
        },
      ];
      this.detailsSidebar.visible = true;
      this.detailsSidebar.selected = DETAIL_SIDEBAR_TYPES.assets;
      this.detailsSidebar.type = DETAIL_SIDEBAR_TYPES.assets;

      await this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
        this.detailsSidebar
      );

      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.assets,
        EVENT_ACTIONS.clicked,
        `Asset View on Map Click --- ${item.id}`
      );
    },
    formatMonitorRadius(radiusKm) {
      const units = this.preferences?.poisDistUnits === "miles" ? "mi" : "km";
      return `${formatDisplayMonitorRadius(
        radiusKm,
        this.preferences.poisDistUnits
      )} ${units}`;
    },
    riskRatingHeaders() {
      const toHeader = (c) => {
        return {
          text: "RR - " + c.display,
          value: headerOptionValues.RISK_RATINGS + "." + c.dbName,
          sortable: true,
          maxWidth: "100",
          width: 100,
          isRiskRating: true,
          riskRatingId: c.id,
          riskRatingParentId: c.parentId ?? null,
          dbName: c.dbName,
          hidden: !(c.id === RISK_RATING_PRIMARIES.security ?? false),
        };
      };

      return this.riskRatingCategories
        ?.filter((c) => !c.parentId)
        ?.map((c) => {
          return {
            ...toHeader(c),
            children: this.riskRatingCategories
              ?.filter((cc) => cc.parentId === c.id)
              ?.map((cc) => toHeader(cc)),
          };
        });
    },
    initialHeaders() {
      return [
        {
          text: "Type",
          value: headerOptionValues.ASSET_TYPE,
          sortable: true,
          width: 55,
          isSticky: true,
        },
        {
          text: "Title",
          value: headerOptionValues.TITLE,
          sortable: true,
          maxWidth: "200",
          width: 200,
          isSticky: true,
        },
        {
          text: "Country",
          value: headerOptionValues.COUNTRY_NAME,
          sortable: true,
          width: 75,
          isSticky: true,
        },
        {
          text: "Monitor Radius",
          value: headerOptionValues.MONITOR_RADIUS,
          sortable: false,
          maxWidth: "100",
          width: 100,
        },
        ...(this.isVEColumnFeatureUnlocked
          ? [
              {
                text: "Verified Events",
                value: headerOptionValues.VERIFIED_EVENTS,
                sortable: true,
                maxWidth: "100",
                width: 100,
                children: [
                  {
                    text: "VE Change",
                    value: headerOptionValues.VERIFIED_EVENTS_DELTA,
                    sortable: true,
                    maxWidth: "110",
                    width: 110,
                  },
                  {
                    text: "VE - Crime",
                    value: headerOptionValues.VERIFIED_EVENTS_CRIME,
                    sortable: true,
                    maxWidth: "100",
                    width: 100,
                    hidden: true,
                  },
                  {
                    text: "VE Change - Crime",
                    value: headerOptionValues.VERIFIED_EVENTS_CRIME_DELTA,
                    sortable: true,
                    maxWidth: "110",
                    width: 110,
                    hidden: true,
                  },
                  {
                    text: "VE - Terrorism",
                    value: headerOptionValues.VERIFIED_EVENTS_TERRORISM,
                    sortable: true,
                    maxWidth: "100",
                    width: 100,
                    hidden: true,
                  },
                  {
                    text: "VE Change - Terrorism",
                    value: headerOptionValues.VERIFIED_EVENTS_TERRORISM_DELTA,
                    sortable: true,
                    maxWidth: "110",
                    width: 110,
                    hidden: true,
                  },
                  {
                    text: "VE - Unrest",
                    value: headerOptionValues.VERIFIED_EVENTS_UNREST,
                    sortable: true,
                    maxWidth: "100",
                    width: 100,
                    hidden: true,
                  },
                  {
                    text: "VE Change - Unrest",
                    value: headerOptionValues.VERIFIED_EVENTS_UNREST_DELTA,
                    sortable: true,
                    maxWidth: "110",
                    width: 110,
                    hidden: true,
                  },
                  {
                    text: "VE - War",
                    value: headerOptionValues.VERIFIED_EVENTS_WAR,
                    sortable: true,
                    maxWidth: "100",
                    width: 100,
                    hidden: true,
                  },
                  {
                    text: "VE Change - War",
                    value: headerOptionValues.VERIFIED_EVENTS_WAR_DELTA,
                    sortable: true,
                    maxWidth: "110",
                    width: 110,
                    hidden: true,
                  },
                ],
              },
            ]
          : []),
        {
          text: "Hotspots",
          value: headerOptionValues.HOTSPOTS,
          sortable: true,
          maxWidth: "100",
          width: 100,
        },
        ...(!this.hasEssentials
          ? [
              {
                text: "Pulse Score (Live)",
                value: headerOptionValues.PULSE,
                sortable: true,
                maxWidth: "100",
                width: 100,
                children: [
                  {
                    text: "Pulse Delta (24hr)",
                    value: headerOptionValues.PULSE_DELTA_1d,
                    sortable: true,
                    maxWidth: "125",
                    width: 125,
                  },
                  {
                    text: "Pulse Delta (7d)",
                    value: headerOptionValues.PULSE_DELTA_7d,
                    sortable: true,
                    maxWidth: "125",
                    width: 125,
                    hidden: true,
                  },
                  {
                    text: "Pulse Delta (30d)",
                    value: headerOptionValues.PULSE_DELTA_30d,
                    sortable: true,
                    maxWidth: "125",
                    width: 125,
                    hidden: true,
                  },
                ],
              },
            ]
          : []),
        ...this.riskRatingHeaders(),
      ];
    },
  },
  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
    "preferences.assets": {
      handler(newVal, oldVal) {
        if (
          lodashIsEqual(newVal, oldVal) &&
          this.isMapControlsFeatureUnlocked
        ) {
          return;
        }
        this.assetsEnabled = lodashGet(this.preferences, "assets.enabled");
        // set preferences to options object
        this.updateOptionsFromFilters();
      },
      immediate: true,
    },
    assetsSelected: {
      handler() {
        this.selected = lodashCloneDeep(this.assetsSelected);
      },
      deep: true,
      immediate: true,
    },
    options: {
      handler: lodashDebounce(async function (val) {
        this.loading = true;
        await this.$store.dispatch(MODULE_PREFIX_ASSETS + GET_ASSETS, {
          options: val,
        });
        await this.refreshMap(true);
        if (this.isMapControlsFeatureUnlocked) {
          await this.$store.dispatch(
            MODULE_PREFIX_ASSETS + GET_ASSETS_TABLE_OPTIONS,
            this.options
          );
        }
      }, 250),
      deep: true,
    },
    filtersSelected: {
      handler() {
        if (this.filtersSelected?.filterTitle) {
          this.options.filterTitle = this.filtersSelected.filterTitle;
        }
        if (this.filtersSelected?.filterCountries) {
          this.options.filterCountries = this.filtersSelected.filterCountries;
        }
        if (this.filtersSelected?.filterAssetTypeIds) {
          this.options.filterAssetTypeIds = this.filtersSelected.filterAssetTypeIds;
        }
        if (this.filtersSelected?.filterRatings) {
          this.options.filterRatings = this.filtersSelected.filterRatings;
        }
      },
      deep: true,
      immediate: true,
    },
    filteredDataFields: {
      handler() {
        if (!this.isInitialized) {
          this.selectedFields = this.filteredDataFields
            .flatMap((field) => {
              if (field.children) {
                return [field, ...field.children];
              }
              return field;
            })
            .filter((field) => {
              return !field.hidden;
            })
            .map((h) => h.value);
        }
      },
      deep: true,
      immediate: true,
    },
    alertId: {
      handler() {
        if (this.alertId) {
          this.options.filterAlertId = this.alertId;
          this.options.sortDirection = sortOptions.DESCENDING;
        }
      },
      immediate: true,
    },
    assets: {
      handler(val) {
        this.loading = false;
        if (val?.metadata) {
          this.pageNum = val.metadata.pageNumber + "";
          this.itemsPerPage = val.metadata.count;
          this.totalAssets = val.metadata.total;
        }
      },
    },
    notification: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal && !this.notification) {
          this.options = {
            filterTitle: null,
            filterCountries: [],
            filterAssetTypeIds: [],
            filterRatings: {},
            filterAlertId: null,
            filterDateRange: {},
            customFieldName: null,
            sortField: null,
            sortDirection: sortOptions.NONE,
          };
        }
      },
    },
    assetsEnabled: {
      async handler() {
        await this.refreshMap();
      },
    },
  },
};
</script>

<style>
.asset-types .v-list-item__title,
.asset-types .v-select__selections .v-list-item__content {
  padding: 0;
}
.asset-types .v-list-item {
  min-height: 32px;
  padding: 0 16px;
}
.assets-data-table .v-data-table-header__icon {
  position: relative;
  top: -2px;
  left: 7px;
}
.assets-data-table .v-data-table-header th {
  white-space: nowrap;
}
.location-textfields .v-messages__message {
  color: #ff5252 !important;
}
.assets-data-table.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td,
.assets-data-table.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > th,
.assets-data-table.v-data-table
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > td,
.assets-data-table.v-data-table
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > th,
.assets-data-table.v-data-table
  > .v-data-table__wrapper
  > table
  > tfoot
  > tr
  > td,
.assets-data-table.v-data-table
  > .v-data-table__wrapper
  > table
  > tfoot
  > tr
  > th {
  padding: 0 5px !important;
  border-right: 1px solid #e2e2e2;
}

.assets-data-table.v-data-table
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > th {
  background: #f2f2f2;
}

/* first child sticky */
.assets-data-table.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:hover
  > td.text-start:first-child {
  background: #eeeeee;
}
.assets-data-table.v-data-table
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > th.text-start:first-child {
  position: sticky !important;
  left: 0 !important;
  top: 0 !important;
  z-index: 11 !important;
  background: #f2f2f2;
}
.assets-data-table.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td.text-start:first-child {
  position: sticky !important;
  left: 0 !important;
  z-index: 10 !important;
  background: #fbfbfb;
}
/* first child sticky end */

/* first 4 = (-n+4) child sticky with action width: column 60px */
.assets-data-table.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:hover
  > td.text-start:nth-child(-n + 4) {
  background: #eeeeee;
}
.assets-data-table.v-data-table
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > th.text-start:nth-child(-n + 4) {
  position: sticky !important;
  top: 0 !important;
  z-index: 11 !important;
  background: #f2f2f2;
}
.assets-data-table.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td.text-start:nth-child(-n + 4) {
  position: sticky !important;
  z-index: 10 !important;
  background: #fbfbfb;
}

.assets-data-table.v-data-table
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > th.text-start:nth-child(2) {
  left: 60px !important;
}
.assets-data-table.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td.text-start:nth-child(2) {
  left: 60px !important;
}
.assets-data-table.v-data-table.selectable
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > th.text-start:nth-child(2) {
  left: 34px !important;
}
.assets-data-table.v-data-table.selectable
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td.text-start:nth-child(2) {
  left: 34px !important;
}
.assets-data-table.v-data-table
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > th.text-start:nth-child(3) {
  left: 115px !important;
}
.assets-data-table.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td.text-start:nth-child(3) {
  left: 115px !important;
}
.assets-data-table.v-data-table.selectable
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > th.text-start:nth-child(3) {
  left: 89px !important;
}
.assets-data-table.v-data-table.selectable
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td.text-start:nth-child(3) {
  left: 89px !important;
}
.assets-data-table.v-data-table
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > th.text-start:nth-child(4) {
  left: 315px !important;
}
.assets-data-table.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td.text-start:nth-child(4) {
  left: 315px !important;
}
.assets-data-table.v-data-table.selectable
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > th.text-start:nth-child(4) {
  left: 289px !important;
}
.assets-data-table.v-data-table.selectable
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td.text-start:nth-child(4) {
  left: 289px !important;
}
/* first 4 = (-n+4) child sticky end */
</style>

<style scoped>
.hyp-table-container {
  width: 97%;
  min-height: 500px;
}
.assets-data-table {
  overflow-y: auto;
}
.asset-title {
  max-width: 190px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}
.asset-action-menu-button:hover,
.asset-action-menu-button:focus {
  color: transparent;
}
.asset-action-menu-icon {
  filter: opacity(40%);
}
.asset-action-menu .v-list-item:hover {
  cursor: pointer;
  background-color: var(--light-theme-menu-hover-color);
}
.v-overlay.copy-success {
  left: -40px !important;
  top: -133px;
  z-index: 99 !important;
}

.pulse-cell .pulse-circle {
  width: 12px;
  height: 12px;
  border-radius: 12px;
  margin-top: 8%;
}
.pulse-cell .pulse-score {
  font-weight: bold;
}
.v-application .primary--text.asset-selector-disabled-field {
  color: #aaaaaa !important;
}
.hyp-text-menu-btn {
  border-radius: unset;
  border-bottom: 1px solid #aaaaaa;
  margin-top: 13px;
}
.hyp-assets-filters-container,
.hyp-assets-menu-container {
  background-color: white;
}
.hyp-assets-menu-container {
  max-height: 60vh;
  overflow-y: auto;
}
.assets-filters-section {
  display: flex;
}
.assets-filters-section .col {
  max-width: fit-content;
}
.assets-filters-section .assets-fields-selector {
  margin-left: auto;
}
.assets-filters-section .assets-filter-title {
  min-width: 200px;
}
</style>
